// v84.5 — Customer Tracking Page (public, mobile-first, no login)
// SSE-based updates. Fallback: manual refresh via visibility change.
import { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { MapPin, Clock, User, Wrench, CheckCircle2, XCircle, Loader2, Truck } from "lucide-react";
import axios from "axios";
import { bearingDeg, enqueueMove, fmtRouteEta, headingMarker, loadLeaflet } from "@/lib/leaflet";
import { PushOptIn } from "@/components/PushOptIn";
import { OpenInChrome } from "@/components/OpenInChrome";

const BASE = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

function fmtTime(iso) {
  if (!iso) return "-";
  try {
    const d = new Date(iso);
    return d.toLocaleTimeString("id-ID", { hour: "2-digit", minute: "2-digit", timeZone: "Asia/Makassar" });
  } catch { return "-"; }
}
function fmtRelative(iso) {
  if (!iso) return "-";
  const d = new Date(iso).getTime();
  const now = Date.now();
  const sec = Math.floor((now - d) / 1000);
  if (sec < 30) return "baru saja";
  if (sec < 60) return `${sec} detik lalu`;
  if (sec < 3600) return `${Math.floor(sec/60)} menit lalu`;
  return `${Math.floor(sec/3600)} jam lalu`;
}

export default function Tracking() {
  const { token } = useParams();
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [now, setNow] = useState(Date.now());
  const esRef = useRef(null);
  const mapRef = useRef(null);
  const leafletMapRef = useRef(null);
  const leafletMarkerRef = useRef(null);
  const routeRef = useRef(null);
  const fitRef = useRef(false);

  // tick tiap 30 detik utk update "last update relatif"
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(t);
  }, []);

  // v84.6 — Leaflet + SlideTo (marker armada bergerak halus) + rute jalan ORS
  useEffect(() => {
    if (!state?.tech_location) return;
    let alive = true;
    loadLeaflet().then((L) => {
      if (!alive || !mapRef.current) return;
      const { lat, lng } = state.tech_location;
      if (!leafletMapRef.current) {
        const map = L.map(mapRef.current, { zoomControl: true }).setView([lat, lng], 15);
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          attribution: "© OpenStreetMap", maxZoom: 19,
        }).addTo(map);
        // v84.133 — Custom armada icon (Toyota Hiace van dgn logo GLOBAL AC)
        // Mobil tampak atas, diputar mengikuti arah jalan (awal: menghadap lokasi pelanggan).
        leafletMarkerRef.current = headingMarker(L, [lat, lng], "/armada-top.png").addTo(map);
        const rc = state.route?.coordinates || [];
        const ahead = rc.find((p) => bearingDeg([lat, lng], p) != null)
          || (state.destination ? [state.destination.lat, state.destination.lng] : null);
        const b0 = ahead ? bearingDeg([lat, lng], ahead) : null;
        if (b0 != null) leafletMarkerRef.current._gacOnHeading(b0);
        leafletMarkerRef.current._gacLast = [lat, lng];
        leafletMarkerRef.current._gacTs = Date.parse(state.tech_location.updated_at) || Date.now();
        leafletMapRef.current = map;
        fitRef.current = false;
      } else {
        // Gerak mulus konstan: luncur selama jeda antar titik (sama dgn Live Map admin).
        const m = leafletMarkerRef.current;
        const last = m._gacLast;
        if (!last || last[0] !== lat || last[1] !== lng) {
          m._gacLast = [lat, lng];
          enqueueMove(m, [lat, lng], Date.parse(state.tech_location.updated_at) || Date.now());
        }
      }
      leafletMarkerRef.current.bindPopup(`<b>${state.armada_name || "Teknisi"}</b><br>${state.tech_location.age_min?.toFixed(1)} min lalu`);
      drawRoute(L, leafletMapRef.current);
    });
    // Rute jalan (OpenRouteService) teknisi → lokasi Anda saat teknisi menuju lokasi
    function drawRoute(L, map) {
      if (routeRef.current) { routeRef.current.forEach(l => map.removeLayer(l)); routeRef.current = null; }
      const d = state?.destination;
      if (!state?.otw || !d) return;
      const pts = state.route?.coordinates?.length >= 2
        ? state.route.coordinates
        : [[state.tech_location.lat, state.tech_location.lng], [d.lat, d.lng]];
      routeRef.current = [
        L.polyline(pts, { color: "#2563EB", weight: 5, opacity: 0.85 }).addTo(map),
        L.circleMarker([d.lat, d.lng], { radius: 7, color: "#1D4ED8", fillColor: "#3B82F6", fillOpacity: 1 })
          .bindTooltip("Lokasi Anda").addTo(map),
      ];
      if (!fitRef.current) {
        try { map.fitBounds(pts, { padding: [40, 40], maxZoom: 16 }); fitRef.current = true; } catch {}
      }
    }
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state?.tech_location, state?.armada_name, state?.otw, state?.destination, state?.route]);

  useEffect(() => {
    let alive = true;
    async function fetchInitial() {
      try {
        const r = await axios.get(`${BASE}/api/public/tracking/${token}`);
        if (alive) { setState(r.data); setLoading(false); }
      } catch (e) {
        if (alive) { setError(e?.response?.status === 404 ? "Tracking tidak ditemukan" : "Gagal memuat"); setLoading(false); }
      }
    }
    fetchInitial();

    // SSE subscription
    try {
      const es = new EventSource(`${BASE}/api/public/tracking/${token}/events`);
      esRef.current = es;
      es.addEventListener("update", (ev) => {
        try {
          const data = JSON.parse(ev.data);
          if (alive) setState(data);
        } catch {}
      });
      es.onerror = () => { /* browser auto-retries; no toast */ };
    } catch {}

    return () => {
      alive = false;
      try { esRef.current?.close(); } catch {}
    };
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-b from-[#F8FAFC] to-[#EEF2F7]">
        <div className="flex items-center gap-2 text-[13px] text-zinc-600">
          <Loader2 className="w-4 h-4 animate-spin" /> Memuat status pesanan…
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center bg-gradient-to-b from-[#F8FAFC] to-[#EEF2F7] p-4">
        <div className="max-w-sm text-center">
          <XCircle className="w-10 h-10 text-red-500 mx-auto mb-2" />
          <div className="text-[15px] font-semibold text-zinc-800">{error}</div>
          <div className="text-[12px] text-zinc-500 mt-1">Coba refresh halaman ini atau hubungi admin.</div>
        </div>
      </div>
    );
  }

  const isDone = state?.status === "DONE";
  const isCancelled = state?.status === "CANCELLED";
  const isInProgress = state?.status === "IN_PROGRESS";
  const isClosed = state?.status === "CLOSED";

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#F8FAFC] to-[#EEF2F7] pb-8" data-testid="tracking-page">
      <PushOptIn token={token} />
      <OpenInChrome />
      {/* Header brand */}
      <div className="bg-white border-b px-4 py-3 shadow-sm sticky top-0 z-10">
        <div className="max-w-md mx-auto flex items-center justify-between">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">GLOBAL AC</div>
            <div className="text-[14px] font-bold text-zinc-800">Tracking Pesanan Anda</div>
          </div>
          <Truck className="w-6 h-6 text-[#2962FF]" />
        </div>
      </div>

      <div className="max-w-md mx-auto p-4 space-y-3">
        {/* Status Big Card */}
        <div className={`rounded-xl border-2 shadow-sm p-5 ${
          isDone ? "bg-emerald-50 border-emerald-300" :
          isCancelled ? "bg-red-50 border-red-300" :
          isClosed ? "bg-zinc-100 border-zinc-300" :
          isInProgress ? "bg-blue-50 border-blue-300" :
          "bg-white border-zinc-200"
        }`} data-testid="tracking-status-card">
          {isDone && (
            <div className="text-center">
              <CheckCircle2 className="w-14 h-14 text-emerald-600 mx-auto mb-2" />
              <div className="text-[20px] font-bold text-emerald-800">Selesai</div>
              <div className="text-[13px] text-emerald-700 mt-1">{state.eta_note || "Terima kasih 🙏"}</div>
            </div>
          )}
          {isCancelled && (
            <div className="text-center">
              <XCircle className="w-14 h-14 text-red-600 mx-auto mb-2" />
              <div className="text-[20px] font-bold text-red-800">Dibatalkan</div>
              <div className="text-[13px] text-red-700 mt-1">Silakan hubungi admin untuk info lebih lanjut.</div>
            </div>
          )}
          {isClosed && (
            <div className="text-center" data-testid="tracking-closed-state">
              <Clock className="w-14 h-14 text-zinc-500 mx-auto mb-2" />
              <div className="text-[20px] font-bold text-zinc-700">Layanan Hari Ini Selesai</div>
              <div className="text-[13px] text-zinc-600 mt-1">{state.eta_note}</div>
            </div>
          )}
          {!isDone && !isCancelled && !isClosed && (
            <div className="text-center">
              <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold">Pesanan Anda</div>
              <div className="mt-1 flex items-baseline justify-center gap-1.5">
                <span className="text-[36px] font-bold text-[#2962FF]" data-testid="queue-position">{state.queue_position}</span>
                <span className="text-[13px] text-zinc-500">dari {state.queue_total}</span>
              </div>
              <div className="text-[12px] text-zinc-600 mt-0.5">urutan hari ini</div>
              {isInProgress ? (
                <div className="mt-4 p-3 bg-white rounded-lg border border-blue-200">
                  <div className="flex items-center justify-center gap-2 text-[13px] font-semibold text-blue-800">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Sedang dikerjakan
                  </div>
                </div>
              ) : (
                <>
                  {state.otw && state.route && (
                    <div className="mt-4 p-3 rounded-lg border-2 border-blue-500 bg-blue-500/15" data-testid="route-eta">
                      <div className="text-[10px] uppercase tracking-widest text-blue-200 font-semibold">Teknisi Menuju Lokasi Anda</div>
                      <div className="text-[22px] font-bold text-white mt-0.5" data-testid="route-eta-text">
                        Tiba {fmtRouteEta(state.route)}
                      </div>
                      <div className="text-[12px] text-blue-100 mt-0.5">
                        Perkiraan pukul {fmtTime(state.route.eta_at)} WITA{state.route.source === "haversine" ? " · perkiraan kasar" : " · mengikuti rute jalan"}
                      </div>
                    </div>
                  )}
                  <div className="mt-4 p-3 bg-white rounded-lg border border-zinc-200">
                    <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Estimasi Kedatangan</div>
                    <div className="text-[22px] font-bold text-zinc-800 mt-0.5" data-testid="eta-range">
                      {fmtTime(state.eta_start)} – {fmtTime(state.eta_end)}
                    </div>
                    <div className="text-[11px] text-zinc-500 mt-0.5">WITA</div>
                    {state.eta_note && (
                      <div className="text-[11px] text-zinc-600 mt-2 italic">{state.eta_note}</div>
                    )}
                  </div>
                  {/* v84.132 — Notice: waktu estimasi dinamis (bingkai orange) */}
                  <div
                    className="mt-2 px-3 py-2 rounded-md border-2 border-orange-500 bg-transparent text-[11px] leading-snug text-white font-medium text-center"
                    data-testid="tracking-eta-notice"
                  >
                    Waktu estimasi dapat berubah secara otomatis oleh sistem, menyesuaikan
                    kondisi operasional teknisi di lapangan
                  </div>
                </>
              )}
            </div>
          )}
        </div>

        {/* v84.6 — Peta Leaflet: tampilkan hanya kalau ada tech_location & belum DONE */}
        {state?.tech_location && !isDone && !isCancelled && (
          <div className="rounded-xl border bg-white shadow-sm overflow-hidden" data-testid="tracking-map-card">
            <div className="px-4 py-2.5 border-b flex items-center justify-between">
              <div>
                <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Lokasi Teknisi</div>
                <div className="text-[11px] text-zinc-500 mt-0.5">
                  Update {state.tech_location.age_min?.toFixed(0)} menit lalu · Akurasi ±{Math.round(state.tech_location.accuracy_m || 0)}m
                </div>
              </div>
              <MapPin className="w-5 h-5 text-emerald-600" />
            </div>
            <div ref={mapRef} style={{ height: "220px", width: "100%" }} data-testid="tracking-map" />
          </div>
        )}

        {/* Detail WO */}
        <div className="rounded-xl border bg-white shadow-sm p-4 space-y-2.5" data-testid="tracking-detail-card">
          <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">Detail Pesanan</div>
          <Row icon={Wrench} label="Layanan" value={state.service_type || "-"} />
          <Row icon={User} label="Nama" value={state.customer_name || "-"} />
          {state.customer_phone && <Row icon={User} label="No HP" value={`•••••${state.customer_phone}`} />}
          <Row icon={Truck} label="Armada Bertugas" value={state.armada_name || "-"} />
          <Row icon={Clock} label="Jadwal Awal" value={state.scheduled_start ? `${fmtTime(state.scheduled_start)} WITA` : "-"} />
        </div>

        {/* Last update */}
        <div className="text-center text-[11px] text-zinc-500 flex items-center justify-center gap-1.5" data-testid="last-update">
          <MapPin className="w-3 h-3" />
          Update terakhir: {fmtRelative(state.last_updated_at)}
          <span className="text-zinc-400">• {new Date(state.last_updated_at).toLocaleTimeString("id-ID", {timeZone: "Asia/Makassar"})}</span>
        </div>

        <div className="text-center text-[10px] text-zinc-400 mt-4">
          Halaman ini otomatis update. Simpan link untuk mengecek kembali.
        </div>
      </div>
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="w-3.5 h-3.5 text-zinc-400 mt-0.5 flex-none" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">{label}</div>
        <div className="text-[13px] text-zinc-800 break-words">{value}</div>
      </div>
    </div>
  );
}
